import { Draft } from 'immer';
import * as _ from 'lodash';

import { finleyAssert } from '@finley-technologies/common';
import { CreditAgreement, Funding } from '@finley-technologies/finley-types';

import { BaseCommitmentMechanicsFormData } from '../types';

type CreditAgreementFullArtifact = CreditAgreement.DomainEntity.CreditAgreementFullArtifact;

// TODO: get rid of this convention and just use UUIDs. This will require a CA type refactor.
export const loanKeyByConvention = (product: { pricingOptionKey: string }) =>
  product.pricingOptionKey;

// This function receives a 'draft' of the Credit Agreement, which is provided by the immer library.
// This means that it is ok to mutate the `draft` variable - this produces a new CA object, and leaves
// the original CA reference untouched.
export const setFormValues = (
  draft: Draft<CreditAgreementFullArtifact>,
  values: BaseCommitmentMechanicsFormData,
) => {
  // It shouldn't be possible for loanMechanics to be undefined at this point
  // but this helps with types.
  draft.loanMechanicsV1 = draft.loanMechanicsV1 || {
    pricingOptions: [],
    loans: [],
    commitments: [],
  };

  // Find the commitment to update
  const commitment = draft.loanMechanicsV1.commitments.find(
    (c) => c.key === values.key,
  );

  if (!commitment) return;

  commitment.name = values.commitmentName;

  if (values.totalCommitment !== null) {
    commitment.limit.amount = values.totalCommitment;
  }
  if (values.maturityDate !== null) {
    commitment.maturityDate = values.maturityDate;
  }

  const servicingFeeMap = new Map(
    values.servicingFees.map((fee) => [fee.lenderId, fee.servicingFee]),
  );

  const commitmentLevelLoans: typeof commitment.loans = [];
  for (const product of values.products) {
    // TODO: some properties of the commitment-level loan in the UI actually
    //  live on the loan mechanics-level loan in the credit agreement. This
    //  needs a type refactor because if there is another commitment referencing
    //  this top level loan, this mutation will be surprising.
    const existingLoanIndex = draft.loanMechanicsV1.loans.findIndex(
      (l) => l.pricingOptionKey === product.pricingOptionKey,
    );
    const existingLoan =
      existingLoanIndex >= 0
        ? draft.loanMechanicsV1.loans[existingLoanIndex]
        : null;
    const loanKey = existingLoan?.key ?? loanKeyByConvention(product);

    const loan: typeof draft.loanMechanicsV1.loans[number] = {
      key: loanKey,
      name: product.name,
      pricingOptionKey: product.pricingOptionKey,
      maturity: {
        type: 'months',
        count: product.maturityMonths ?? 0,
      },
      interest: { calculation: { dayCountConvention: product.accrualBasis } },
    };

    if (existingLoan) {
      draft.loanMechanicsV1.loans[existingLoanIndex] = loan;
    } else {
      draft.loanMechanicsV1.loans.push(loan);
    }

    // Note: it is intentional that removing a loan from `commitment.loans` does _not_ remove the
    // top-level loan from `loanMechanicsV1.loans`. There may be foreign keys referencing the loan
    // key, and it's good to have a record of it.
    commitmentLevelLoans.push({
      name: product.name,
      loanKey,
      lenders: values.lenders.map((lender) => {
        const result: Funding.LoanMechanics['commitments'][number]['loans'][number]['lenders'][number] = {
          entity: {
            // Need to add every lender to every loan
            id: lender.lenderId,
            name: lender.lenderName,
            slug: lender.lenderSlug,
          },
          amount: lender.proRataAmount ?? 0,
        };

        const correspondingServicingFee = servicingFeeMap.get(lender.lenderId);

        if (correspondingServicingFee !== undefined) {
          result.servicerFee = {
            type: 'rate',
            rate: correspondingServicingFee ?? 0,
          };
        }

        return result;
      }),
    });
  }
  commitment.loans = commitmentLevelLoans;

  // ** update total commitment amount for each capital provider
  const lenderIdToAmount: Record<string, number> = {};

  draft.loanMechanicsV1.commitments
    .filter((c) => !c.limit.sublimit)
    .forEach((c) => {
      if (!c.loans[0]) return;

      // ** assert all lenders are the same across commitment's loans
      const defaultLenders = c.loans[0].lenders;
      c.loans.forEach((l) =>
        finleyAssert(
          _.isEqual(defaultLenders, l.lenders),
          `All lenders must be the same across all loans of a commitment [key: ${c.key}].`,
        ),
      );

      c.loans[0].lenders.forEach((lender) => {
        if (!(lender.entity.id in lenderIdToAmount)) {
          lenderIdToAmount[lender.entity.id] = 0;
        }
        lenderIdToAmount[lender.entity.id] += lender.amount;
      });
    });

  Object.entries(lenderIdToAmount).forEach(
    ([lenderId, lenderCommitmentAmount]) => {
      const cp = draft.facilityMechanics.parties.capitalProviders.find(
        (lender) => lender.entity.id === lenderId,
      );

      if (cp) cp.commitmentAmount.value = lenderCommitmentAmount;
    },
  );
};
